import { Box, FormControl, InputLabel, MenuItem, Select, Typography } from "@mui/material";
import { LineChart } from "@mui/x-charts/LineChart";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import ChartCard from "../reports/ChartCard";

const PROFILE_CHART_YEAR_MIN = 2015;
const PROFILE_CHART_YEAR_MAX = 2027;

const profileChartYears = Array.from(
  { length: PROFILE_CHART_YEAR_MAX - PROFILE_CHART_YEAR_MIN + 1 },
  (_, i) => PROFILE_CHART_YEAR_MAX - i
);

const ProfileRepliesChart = ({ data, isRTL, selectedYear, onYearChange }) => {
  const { t } = useTranslation();

  const { xLabels, counts, hasData } = useMemo(() => {
    const items = data || [];
    const labels = items.map((m) => (isRTL ? m.label_ar : m.label_en));
    const values = items.map((m) => m.count);
    return {
      xLabels: labels,
      counts: values,
      hasData: values.some((v) => v > 0),
    };
  }, [data, isRTL]);

  const useRotatedLabels = xLabels.length > 6;

  const yearSelector = (
    <FormControl size="small" sx={{ minWidth: 140, mb: 2 }}>
      <InputLabel id="profile-replies-year-label">
        {t("profile.chart.year")}
      </InputLabel>
      <Select
        labelId="profile-replies-year-label"
        value={selectedYear ?? ""}
        label={t("profile.chart.year")}
        onChange={(e) => {
          const val = e.target.value;
          onYearChange(val === "" ? null : Number(val));
        }}
      >
        <MenuItem value="">{t("profile.chart.allYears")}</MenuItem>
        {profileChartYears.map((y) => (
          <MenuItem key={y} value={y}>
            {y}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  const yearSelectorOffset = 56;

  return (
    <ChartCard
      title={t("profile.chart.repliesTitle")}
      subtitle={t("profile.chart.repliesSubtitle")}
      isRTL={isRTL}
      height={400}
      exportCategories={xLabels}
      exportSeries={[{ name: t("profile.chart.replyCount"), data: counts }]}
    >
      {(layoutHeight, _expanded, showLabels) => (
        <Box>
          {yearSelector}
          {!hasData ? (
            <Box
              sx={{
                height: layoutHeight - yearSelectorOffset,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Typography color="text.secondary">{t("profile.noData")}</Typography>
            </Box>
          ) : (
            <LineChart
              xAxis={[
                {
                  scaleType: "point",
                  data: xLabels,
                  height: "auto",
                  tickLabelStyle: {
                    fontSize: 11,
                    ...(useRotatedLabels
                      ? { angle: -35, textAnchor: "end" }
                      : { textAnchor: "middle" }),
                  },
                },
              ]}
              yAxis={[
                {
                  width: 56,
                  tickLabelStyle: { textAnchor: "end", fontSize: 11 },
                },
              ]}
              series={[
                {
                  data: counts,
                  label: t("profile.chart.replyCount"),
                  color: "#1976d2",
                  showMark: true,
                  curve: "natural",
                },
              ]}
              height={layoutHeight - yearSelectorOffset}
              margin={{
                top: showLabels ? 28 : 20,
                bottom: 16,
                left: 8,
                right: 16,
              }}
              grid={{ horizontal: true }}
              slots={{
                mark: (props) => {
                  const { x, y, color, dataIndex, hidden } = props;
                  if (hidden) {
                    return null;
                  }
                  return (
                    <g>
                      <circle cx={x} cy={y} r={3.5} fill={color} />
                      {showLabels && counts[dataIndex] != null && (
                        <text
                          x={x}
                          y={Number(y) - 10}
                          textAnchor="middle"
                          fontSize={11}
                          fill={color}
                        >
                          {counts[dataIndex]}
                        </text>
                      )}
                    </g>
                  );
                },
              }}
            />
          )}
        </Box>
      )}
    </ChartCard>
  );
};

export default ProfileRepliesChart;
